import { useState, useEffect, useCallback, useRef } from 'react';
import documentCustomizationService from '../services/documentCustomizationService';
import { DocumentCustomization, DEFAULT_CUSTOMIZATION } from '../types/documentCustomization.types';

export const useDocumentCustomization = () => {
  const [customization, setCustomization] = useState<DocumentCustomization>(DEFAULT_CUSTOMIZATION);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Charger la personnalisation au montage
   */
  useEffect(() => {
    loadCustomization();

    // Cleanup: annuler les timers en cours lors du démontage
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const loadCustomization = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentCustomizationService.getCustomization();
      setCustomization(data || DEFAULT_CUSTOMIZATION);
      setHasChanges(false);
    } catch (err: any) {
      console.error('Erreur lors du chargement:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
      // Utiliser les valeurs par défaut en cas d'erreur
      setCustomization(DEFAULT_CUSTOMIZATION);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Mettre à jour la personnalisation (local + backend)
   */
  const updateCustomization = useCallback(async (updates: Partial<DocumentCustomization>) => {
    try {
      setError(null);

      // Mise à jour locale immédiate (optimistic update)
      setCustomization((prev) => ({ ...prev, ...updates }));
      setHasChanges(true);

      // Annuler le timer précédent si existant
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Créer un nouveau timer pour debouncer l'appel API
      debounceTimerRef.current = setTimeout(async () => {
        try {
          setSaving(true);
          // Mise à jour backend avec les dernières valeurs
          const updated = await documentCustomizationService.updateCustomization(updates);
          setCustomization(updated);
          setHasChanges(false);
        } catch (err: any) {
          console.error('Erreur lors de la mise à jour:', err);
          const errorMessage = err.message?.includes('timeout')
            ? 'La requête a pris trop de temps. Veuillez vérifier votre connexion ou réessayer.'
            : err.response?.data?.message || 'Erreur lors de la mise à jour';
          setError(errorMessage);
          // Recharger depuis le backend en cas d'erreur
          await loadCustomization();
        } finally {
          setSaving(false);
        }
      }, 1000); // Attendre 1 seconde après le dernier changement
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour locale:', err);
      setError('Erreur lors de la mise à jour');
    }
  }, []);

  /**
   * Uploader un logo
   */
  const uploadLogo = useCallback(async (file: File) => {
    try {
      setSaving(true);
      setError(null);

      const result = await documentCustomizationService.uploadLogo(file);

      // Recharger la personnalisation pour obtenir le nouveau logoUrl
      await loadCustomization();

      return result;
    } catch (err: any) {
      console.error('Erreur lors de l\'upload:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'upload du logo');
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  /**
   * Supprimer le logo
   */
  const deleteLogo = useCallback(async () => {
    try {
      setSaving(true);
      setError(null);

      await documentCustomizationService.deleteLogo();
      await loadCustomization();
    } catch (err: any) {
      console.error('Erreur lors de la suppression:', err);
      setError(err.response?.data?.message || 'Erreur lors de la suppression du logo');
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  /**
   * Réinitialiser aux valeurs par défaut
   */
  const resetToDefault = useCallback(async () => {
    try {
      setSaving(true);
      setError(null);

      const defaults = await documentCustomizationService.resetToDefault();
      setCustomization(defaults);
      setHasChanges(false);
    } catch (err: any) {
      console.error('Erreur lors de la réinitialisation:', err);
      setError(err.response?.data?.message || 'Erreur lors de la réinitialisation');
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  /**
   * Générer un PDF de test
   */
  const generateTestPDF = useCallback(async (type: 'facture' | 'devis') => {
    try {
      setSaving(true);
      setError(null);

      const blob = await documentCustomizationService.generateTestPDF(type);

      // Ouvrir le PDF dans un nouvel onglet
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');

      // Nettoyer l'URL après quelques secondes
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err: any) {
      console.error('Erreur lors de la génération du PDF:', err);
      setError(err.response?.data?.message || 'Erreur lors de la génération du PDF de test');
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    customization,
    loading,
    saving,
    error,
    hasChanges,
    updateCustomization,
    uploadLogo,
    deleteLogo,
    resetToDefault,
    generateTestPDF,
    reload: loadCustomization,
  };
};
